/**
 * ララリストモーダル Web Component
 * 
 * モーダルの開閉、キーボード操作、フォーカス管理を行います。
 */

class LeleListModal extends HTMLElement {
  constructor() {
    super();
    
    // 状態管理
    this.modals = null;
    this.currentModal = null;
    this.previousFocusedElement = null;
    
    // フォーカス管理
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    
    // バインド
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
  }
  
  /**
   * コンポーネントがDOMに追加された時の処理
   */
  connectedCallback() {
    this.init();
  }
  
  /**
   * 初期化
   */
  init() {
    // モーダル要素を取得
    this.modals = this.querySelectorAll('.c-lele-list-modal');
    
    if (!this.modals.length) {
      console.warn('LeleListModal: モーダル要素が見つかりません');
      return;
    }
    
    // イベントリスナーを設定
    this.setupTriggers();
    this.setupCloseButtons();
    this.setupOverlays();
  }
  
  /**
   * トリガーボタンにイベントリスナーを設定
   */
  setupTriggers() {
    const triggers = document.querySelectorAll('[data-modal-trigger]');
    
    if (!triggers.length) {
      console.warn('LeleListModal: トリガーボタンが見つかりません');
      return;
    }
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const blockId = e.currentTarget.dataset.modalTrigger;
        const modalId = `modal-${blockId}`;
        this.openModal(modalId);
      });
      
      // Enterキーでも開けるように
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const blockId = e.currentTarget.dataset.modalTrigger;
          const modalId = `modal-${blockId}`;
          this.openModal(modalId);
        }
      });
    });
  }
  
  /**
   * 閉じるボタンにイベントリスナーを設定
   */
  setupCloseButtons() {
    const closeButtons = this.querySelectorAll('[data-modal-close]');
    
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    });
  }
  
  /**
   * オーバーレイにイベントリスナーを設定
   */
  setupOverlays() {
    const overlays = this.querySelectorAll('.c-lele-list-modal__overlay');
    
    overlays.forEach(overlay => {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    });
  }
  
  /**
   * モーダルを開く
   * @param {string} modalId - モーダルのID
   */
  openModal(modalId) {
    const modal = this.querySelector(`#${modalId}`);
    
    if (!modal) {
      console.error(`LeleListModal: モーダル #${modalId} が見つかりません`);
      return;
    }
    
    // 既に開いている場合は何もしない
    if (this.currentModal === modal) {
      return;
    }
    
    // 現在フォーカスされている要素を保存
    this.previousFocusedElement = document.activeElement;
    
    // モーダルを表示
    this.currentModal = modal;
    modal.removeAttribute('hidden');
    
    // アニメーション用に少し遅延
    requestAnimationFrame(() => {
      modal.classList.add('c-lele-list-modal--open');
    });
    
    // bodyスクロールを無効化
    this.disableBodyScroll();
    
    // キーボードイベント登録
    document.addEventListener('keydown', this.handleKeydown);
    
    // フォーカス管理をセットアップ
    this.setupFocusTrap(modal);
    
    // 最初のフォーカス可能な要素にフォーカス
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
    
    // ARIAステート更新
    modal.setAttribute('aria-hidden', 'false');
  }
  
  /**
   * モーダルを閉じる
   */
  closeModal() {
    if (!this.currentModal) {
      return;
    }
    
    const modal = this.currentModal;
    
    // アニメーション開始
    modal.classList.remove('c-lele-list-modal--open');
    
    // アニメーション完了後に非表示
    setTimeout(() => {
      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      this.currentModal = null;
    }, 250); // CSSのtransition時間と同じ
    
    // bodyスクロールを復元
    this.enableBodyScroll();
    
    // キーボードイベント削除
    document.removeEventListener('keydown', this.handleKeydown);
    
    // フォーカスを元の位置に戻す
    if (this.previousFocusedElement && this.previousFocusedElement.focus) {
      this.previousFocusedElement.focus();
    }
    
    // クリーンアップ
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.previousFocusedElement = null;
  }
  
  /**
   * bodyスクロールを無効化
   */
  disableBodyScroll() {
    // 現在のスクロール位置を保存
    this.scrollY = window.scrollY;
    
    // bodyにスタイルを適用
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  
  /**
   * bodyスクロールを復元
   */
  enableBodyScroll() {
    // bodyのスタイルを削除
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    
    // スクロール位置を復元
    if (this.scrollY !== undefined) {
      window.scrollTo(0, this.scrollY);
      this.scrollY = undefined;
    }
  }
  
  /**
   * キーボードイベントハンドラ
   * @param {KeyboardEvent} e - キーボードイベント
   */
  handleKeydown(e) {
    // ESCキーでモーダルを閉じる
    if (e.key === 'Escape') {
      this.handleEscape(e);
      return;
    }
    
    // Tabキーでフォーカストラップ
    if (e.key === 'Tab') {
      this.handleTabKey(e);
      return;
    }
  }
  
  /**
   * ESCキーハンドラ
   * @param {KeyboardEvent} e - キーボードイベント
   */
  handleEscape(e) {
    e.preventDefault();
    this.closeModal();
  }
  
  /**
   * フォーカストラップをセットアップ
   * @param {HTMLElement} modal - モーダル要素
   */
  setupFocusTrap(modal) {
    // フォーカス可能な要素のセレクター
    const focusableSelectors = [
      'a[href]:not([disabled])',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    
    // モーダル内のフォーカス可能な要素を取得
    this.focusableElements = Array.from(
      modal.querySelectorAll(focusableSelectors)
    ).filter(element => {
      // 非表示要素を除外
      return element.offsetParent !== null;
    });
    
    if (this.focusableElements.length === 0) {
      console.warn('LeleListModal: フォーカス可能な要素が見つかりません');
      return;
    }
    
    // 最初と最後のフォーカス可能要素を保存
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = 
      this.focusableElements[this.focusableElements.length - 1];
  }
  
  /**
   * Tabキーハンドラ（フォーカストラップ）
   * @param {KeyboardEvent} e - キーボードイベント
   */
  handleTabKey(e) {
    if (!this.focusableElements.length) {
      return;
    }
    
    // Shift + Tab（逆方向）
    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    }
    // Tab（順方向）
    else {
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }
  
  /**
   * コンポーネントがDOMから削除された時の処理
   */
  disconnectedCallback() {
    // イベントリスナーのクリーンアップ
    document.removeEventListener('keydown', this.handleKeydown);
    
    // bodyスクロールを復元
    this.enableBodyScroll();
    
    // 参照をクリア
    this.modals = null;
    this.currentModal = null;
    this.previousFocusedElement = null;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
  }
}

/**
 * Web Componentとして登録
 */
if (!customElements.get('lele-list-modal')) {
  customElements.define('lele-list-modal', LeleListModal);
}

/**
 * デバッグ用ログ（開発環境のみ）
 */
if (window.Shopify && window.Shopify.designMode) {
  console.log('LeleListModal Web Component registered');
}
